/**
 * Server→client carrier for the authed share-link flow.
 *
 * `/s/[id]` server-redirects authed users straight onto the live overlay for
 * the shared pick. The destination data needs to ride from the server (slip
 * loaded from DB) to the client (PlayerSelectionView) without polluting the
 * URL — URLs are bookmarked, restored from history, and indexed by browsers,
 * so any "open this overlay" payload in the URL re-fires every time the user
 * lands on that path.
 *
 * A short-lived non-HttpOnly cookie is the natural fit: the server can set it
 * atomically with the redirect, the client reads + clears it on the next page
 * mount, and it never enters the URL. The localStorage carrier
 * (`slipDestinationStorage.ts`) handles the unauthed→signup→checkout flow
 * separately because that path needs to survive multiple page reloads and
 * external redirects (Stripe).
 *
 * Cookie shape: same `SlipDestination` schema as the localStorage carrier so
 * `useSlipDeepLink` consumes both via a single code path.
 */

import { type SlipDestination, parseSlipSide } from './slipDestinationStorage';

export const SLIP_DESTINATION_COOKIE = 'sharpr_slip_dest';

// 60s is plenty for the redirect → page load → mount → read cycle. Short TTL
// limits the blast radius if the cookie ever leaks into a request the client
// doesn't immediately consume (e.g., user opens a second tab). The client
// also clears the cookie on read.
export const SLIP_DESTINATION_COOKIE_MAX_AGE = 60;

/**
 * Serialize a destination for cookie storage. JSON over the wire — tiny
 * payload (well under 4KB cookie limit) and matches the localStorage shape.
 */
export function serializeSlipDestination(d: SlipDestination): string {
  return JSON.stringify(d);
}

/**
 * Read + parse the slip-destination cookie on the client. Returns `null` when
 * the cookie is missing, malformed, or fails the same shape validation the
 * localStorage hydrator does — defensive parsing because cookies cross trust
 * boundaries (any same-origin script can write them).
 */
export function readSlipDestinationCookie(): SlipDestination | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${SLIP_DESTINATION_COOKIE}=`));
  if (!match) return null;
  const raw = decodeURIComponent(match.slice(SLIP_DESTINATION_COOKIE.length + 1));
  try {
    const parsed = JSON.parse(raw) as Partial<SlipDestination>;
    if (!parsed.sport || !parsed.playerId || !parsed.market) return null;
    return {
      sport: parsed.sport,
      playerId: parsed.playerId,
      market: parsed.market,
      book: parsed.book,
      side: parseSlipSide(parsed.side),
    };
  } catch {
    return null;
  }
}

/**
 * Expire the cookie immediately. Called after a successful read so back-nav
 * to the same path doesn't re-open the overlay. Path must match the set call
 * (`/`) — browsers treat path-mismatched expirations as no-ops.
 */
export function clearSlipDestinationCookie(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${SLIP_DESTINATION_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
}
